import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Box, CircularProgress, Typography, Alert } from '@mui/material';
import { apiService } from '../services/apiService';
import { canAccessManageAccount } from '../utils/permissions';

interface AdminProtectedRouteProps {
  children: React.ReactNode;
}

/**
 * Route protection for admin-only pages (Manage Account module)
 * Checks if user is authenticated and has ADMIN or Admin + Project Manager (ADMIN_MEMBER) role
 * ADMIN: Can only access Manage Account
 * ADMIN_MEMBER (Admin + Project Manager): Can access everything including Manage Account
 */
const AdminProtectedRoute: React.FC<AdminProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated, isLoading: authLoading, user } = useAuth();
  const location = useLocation();
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    const checkAdminRole = async () => {
      if (!isAuthenticated) {
        setIsAdmin(false);
        setIsChecking(false);
        return;
      }

      // Check role from AuthContext first
      const userRole = user?.role;
      if (canAccessManageAccount(userRole)) {
        setIsAdmin(true);
        setIsChecking(false);
        return;
      }

      // Fallback: Try to fetch team members - if successful, user is admin
      // If 403, user is not admin
      try {
        await apiService.getTeamMembers();
        setIsAdmin(true);
      } catch (error: any) {
        if (error.response?.status === 403 || error.response?.status === 401) {
          setIsAdmin(false);
        } else {
          // Other errors - still allow access, API will handle it
          setIsAdmin(true);
        }
      } finally {
        setIsChecking(false);
      }
    };

    if (!authLoading) {
      checkAdminRole();
    }
  }, [isAuthenticated, authLoading, user]);

  // Show loading spinner while checking authentication
  if (authLoading || isChecking) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          width: '100vw',
          gap: 2,
        }}
      >
        <CircularProgress />
        <Typography variant="body2" color="text.secondary">
          Checking permissions...
        </Typography>
      </Box>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Show access denied if not admin
  if (isAdmin === false) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography variant="h6">Access Denied</Typography>
          <Typography variant="body2">
            You do not have permission to access this page. Admin access is required.
          </Typography>
        </Alert>
      </Box>
    );
  }

  return <>{children}</>;
};

export default AdminProtectedRoute;

